import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  ArrowLeft, 
  Loader2, 
  AlertCircle, 
  RefreshCw, 
  Calendar as CalendarIcon, 
  Percent, 
  CircleDollarSign, 
  X, 
  Info,
  ChevronLeft,
  ChevronRight,
  Tag
} from 'lucide-react';
import { useCreateServiceOfferMutation, useLazyGetAllServiceQuery, useLazyGetServiceOfferQuery } from '../../../redux/actions/Admin/ServiceAdmin';
import { IServiceOfferResponse, IserviceResponse } from '../../../types/Response/AdminResponse';
import { ICreateServiceOfferRequest, OfferType } from '../../../types/Request/AdminRequest';

const getDaysInMonthMatrix = (year: number, month: number) => {
  const date = new Date(year, month, 1);
  const days = [];
  const firstDayIndex = date.getDay(); // 0 = Sunday
  
  for (let i = 0; i < firstDayIndex; i++) {
    days.push(null);
  }
  
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const formatLocalDateStr = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export default function ManageOffers() {
  const [triggerGetAllServices, { data: services = [], isLoading: servicesLoading, isError: servicesError }] = useLazyGetAllServiceQuery();
  const [triggerGetOffers, { data: offersResponse = [], isLoading: offersLoading, isError: offersError }] = useLazyGetServiceOfferQuery();
  const [createServiceOffer, { isLoading: isCreating }] = useCreateServiceOfferMutation();

  const [view, setView] = useState<'services-select' | 'offers-workspace'>('services-select');
  const [selectedService, setSelectedService] = useState<IserviceResponse | null>(null);
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDayStr, setSelectedDayStr] = useState<string>(formatLocalDateStr(new Date()));

  const [formData, setFormData] = useState<Omit<ICreateServiceOfferRequest, 'serviceId'>>({
    servicePackageId: null,
    offerType: OfferType.Percentage,
    offerValue: 0,
    offerTitle: '',
    description: '',
    startDate: '',
    endDate: ''
  });

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  
  const monthStartStr = `${year}-${String(month + 1).padStart(2, '0')}-01`;
  const monthEndStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;

  // Fetch initial services data loop on component mount
  useEffect(() => {
    triggerGetAllServices();
  }, [triggerGetAllServices]);

  const offersData = Array.isArray(offersResponse) 
    ? offersResponse 
    : (offersResponse as any)?.bookingData || [];

  const fetchOffersForScope = (serviceId: number) => {
    triggerGetOffers({
      serviceId,
      startDate: monthStartStr,
      endDate: monthEndStr
    });
  };

  useEffect(() => {
    if (selectedService) {
      fetchOffersForScope(selectedService.id);
    }
  }, [currentDate, selectedService]);

  const handleSelectService = (service: IserviceResponse) => {
    setSelectedService(service);
    setView('offers-workspace');
    setSelectedDayStr(formatLocalDateStr(new Date()));
  };

  const handleBackToServices = () => {
    setSelectedService(null);
    setView('services-select');
    setShowCreateModal(false);
  };

  const handleMonthNavigation = (direction: 'prev' | 'next') => {
    setCurrentDate(prev => {
      const nextDate = new Date(prev);
      nextDate.setMonth(direction === 'next' ? prev.getMonth() + 1 : prev.getMonth() - 1);
      return nextDate;
    });
  };

  const handleCreateOfferSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedService || !formData.offerTitle || formData.offerValue <= 0 || !formData.startDate || !formData.endDate) return;

    const payload: ICreateServiceOfferRequest = {
      serviceId: selectedService.id,
      ...formData,
      servicePackageId: formData.servicePackageId ? Number(formData.servicePackageId) : null,
      offerValue: Number(formData.offerValue)
    };

    try {
      await createServiceOffer(payload).unwrap();
      setShowCreateModal(false);
      setFormData({ servicePackageId: null, offerType: OfferType.Percentage, offerValue: 0, offerTitle: '', description: '', startDate: '', endDate: '' });
      fetchOffersForScope(selectedService.id);
    } catch (err) {
      console.error("Failed to compile promotional offer mutation layout:", err);
    }
  };

  const calendarDays = getDaysInMonthMatrix(year, month);
  const sortedServices = [...services].sort((a, b) => a.name.localeCompare(b.name));

  const selectedDayOffers = Array.isArray(offersData)
    ? offersData.filter((o: IServiceOfferResponse) => o.date?.split('T')[0] === selectedDayStr)
    : [];

  return (
    <div className="min-h-screen px-4 py-6 md:p-8">
      
      {/* VIEW 1: SELECT SERVICE LAYER */}
      {view === 'services-select' && (
        <div className="max-w-[1400px] mx-auto">
          <div className="border-b border-gray-300/40 pb-6 mb-8 md:mb-12">
            <h1 className="text-2xl md:text-3xl font-bold text-logoHeading">Campaigns & Offers</h1>
            <p className="text-gray-400 mt-1 text-xs md:text-sm font-medium">
              Select a category to map promotional discounts across operational calendars.
            </p>
          </div>

          {servicesLoading ? (
            <div className="flex flex-col items-center justify-center h-[250px]">
              <Loader2 className="w-8 h-8 animate-spin text-mainBrandColor" />
              <p className="mt-3 text-xs text-gray-400 font-semibold tracking-wider uppercase">Loading Core Matrix...</p>
            </div>
          ) : servicesError ? (
            <div className="bg-white rounded-2xl p-6 text-center border border-mediumgray max-w-md mx-auto shadow-sm">
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
              <h2 className="mt-4 text-sm md:text-base font-bold text-gray-800">Operational Timeout Error</h2>
              <button 
                onClick={() => triggerGetAllServices()} 
                className="mt-4 bg-logoHeading text-white px-5 py-2 rounded-xl text-xs font-semibold cursor-pointer hover:opacity-90 transition"
              >
                Retry Connection
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-x-8 md:gap-y-12 justify-center">
              {sortedServices.map((service) => (
                <div
                  key={service.id}
                  onClick={() => handleSelectService(service)}
                  className="group flex flex-col items-center cursor-pointer transition-all duration-300 relative bg-white p-3 md:p-0 rounded-2xl md:bg-transparent border border-gray-100 md:border-0 shadow-xs md:shadow-none"
                >
                  <div className="relative w-full aspect-square max-w-[140px] md:max-w-[210px]">
                    <div className={`w-full h-full overflow-hidden border-[2px] md:border-[3px] transition-all duration-500 bg-gray-50 
                      ${service.isActive 
                        ? 'border-mainBrandColor rounded-[48%_52%_68%_32%_/_42%_48%_52%_58%] group-hover:rounded-[52%_48%_35%_65%_/_48%_55%_45%_52%] group-hover:shadow-md' 
                        : 'border-gray-200 rounded-[50%] opacity-60'
                      }`}
                    >
                      <img
                        src={service.logoUrl || 'https://images.unsplash.com/photo-1517849845537-4d257902454a?w=400'}
                        alt={service.name}
                        className={`w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${!service.isActive ? 'grayscale' : ''}`}
                      />
                    </div>
                  </div>
                  <div className="mt-3 md:mt-4 text-center space-y-0.5 md:space-y-1 w-full px-1">
                    <h3 className="text-sm md:text-lg font-bold text-logoHeading group-hover:text-mainBrandColor transition-colors duration-200 line-clamp-1">
                      {service.name}
                    </h3>
                    <span className="text-[9px] md:text-[10px] font-extrabold uppercase tracking-widest text-gray-400 group-hover:text-logoHeading transition-colors block">
                      Manage Offers &rarr;
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* =========================================================
          VIEW 2: CALENDAR TIMELINE WORKSPACE
          ========================================================= */}
      {view === 'offers-workspace' && selectedService && (
        <div className="max-w-[1200px] mx-auto space-y-6">
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-gray-300/40 pb-5">
            <div className="space-y-1">
              <button
                onClick={handleBackToServices}
                className="inline-flex items-center gap-1 text-xs font-bold text-gray-400 hover:text-logoHeading uppercase tracking-wider transition cursor-pointer"
              >
                <ArrowLeft className="w-3.5 h-3.5 md:w-4 md:h-4 group-hover:-translate-x-0.5 transition-transform stroke-[2.5]" />
                Back to Services
              </button>
              <h1 className="text-xl md:text-2xl font-black text-logoHeading tracking-tight">
                {selectedService.name} <span className="font-semibold text-gray-400">/ Promotional Matrix</span>
              </h1>
            </div>

            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-logoHeading hover:opacity-95 text-white px-5 py-2.5 rounded-xl text-xs uppercase font-bold tracking-wider shadow-xs transition inline-flex items-center gap-2 cursor-pointer w-full sm:w-auto justify-center"
            >
              <Plus className="w-4 h-4 stroke-[3]" /> Create Offer Entry
            </button>
          </div>

          {/* CALENDAR DESIGN COMPONENT CARD */}
          <div className="bg-white rounded-2xl md:rounded-3xl border border-mediumgray shadow-sm overflow-hidden p-4 md:p-6 space-y-4">
            
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <div className="flex items-center gap-2 text-logoHeading font-black text-base md:text-xl">
                <CalendarIcon className="w-5 h-5 text-mainBrandColor" />
                <span>{currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
              </div>
              <div className="inline-flex items-center gap-1 border border-mediumgray rounded-xl bg-lightgray p-1">
                <button onClick={() => handleMonthNavigation('prev')} className="p-1.5 hover:bg-white rounded-lg transition cursor-pointer"><ChevronLeft className="w-3.5 h-3.5 text-gray-600" /></button>
                <button onClick={() => handleMonthNavigation('next')} className="p-1.5 hover:bg-white rounded-lg transition cursor-pointer"><ChevronRight className="w-3.5 h-3.5 text-gray-600" /></button>
              </div>
            </div>

            {offersLoading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-7 h-7 text-mainBrandColor animate-spin" />
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mt-3">Compiling Campaigns Map...</p>
              </div>
            ) : offersError ? (
              <div className="p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 flex items-center gap-3 text-xs font-semibold">
                <Info className="w-4 h-4 shrink-0" />
                <span>Error syncing scheduled offer arrays from backend channels.</span>
              </div>
            ) : (
              <div>
                <div className="grid grid-cols-7 gap-y-3 gap-x-1 md:gap-x-2 text-center text-[11px] md:text-xs font-bold uppercase tracking-wider text-gray-400 mb-4 py-2 bg-lightgray rounded-lg">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(day => <div key={day}>{day}</div>)}
                </div>

                {/* THE RESPONSIVE CALENDAR GRID */}
                <div className="grid grid-cols-7 gap-y-4 gap-x-1 md:gap-2">
                  {calendarDays.map((day, index) => {
                    if (!day) return <div key={`empty-${index}`} className="w-full aspect-square" />;
                    
                    const dateStr = formatLocalDateStr(day);
                    const isSelected = dateStr === selectedDayStr;
                    
                    const targetOffers = Array.isArray(offersData) 
                      ? offersData.filter((o: IServiceOfferResponse) => o.date?.split('T')[0] === dateStr)
                      : [];

                    const hasOfferActive = targetOffers.length > 0;

                    return (
                      <div 
                        key={dateStr}
                        onClick={() => setSelectedDayStr(dateStr)}
                        className="w-full aspect-square flex flex-col items-center justify-center relative cursor-pointer group"
                      >
                        <div 
                          className={`w-8 h-8 sm:w-12 sm:h-12 flex items-center justify-center rounded-full text-xs md:text-sm font-black transition-all relative ${
                            isSelected 
                              ? 'bg-logoHeading text-white shadow-md' 
                              : hasOfferActive
                                ? 'bg-amber-100/70 text-orange-700 border-2 border-offerColor shadow-2xs'
                                : 'bg-white text-slate-700 border border-gray-200 hover:border-gray-400'
                          }`}
                        >
                          {day.getDate()}

                          {hasOfferActive && !isSelected && (
                            <span className="absolute bottom-1 w-1 h-1 rounded-full bg-orange-600 hidden md:block" />
                          )}
                        </div>

                        {hasOfferActive && (
                          <div className="hidden md:block w-full text-center mt-1 px-1">
                            <p className="text-[10px] font-black text-amber-600 truncate bg-amber-50 rounded px-1 max-w-full">
                              {targetOffers[0].offerTitle}
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* DETAILED CAMPAIGNS STACK BELOW */}
                <div className="mt-6 pt-5 border-t border-gray-100">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-4 flex items-center gap-1.5">
                    <Tag className="w-4 h-4 text-mainBrandColor" />
                    Campaigns for Selected Date ({selectedDayStr})
                  </h4>
                  
                  {selectedDayOffers.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {selectedDayOffers.map((offer: any, idx: number) => (
                        <div key={idx} className="bg-softCream border border-mediumgray rounded-2xl p-4 flex items-center justify-between gap-4 shadow-3xs transition hover:border-gray-300">
                          <div className="space-y-1 min-w-0 flex-1">
                            <p className="text-sm font-black text-logoHeading uppercase tracking-wide truncate">{offer.offerTitle}</p>
                            <p className="text-xs text-gray-500 font-medium line-clamp-2 leading-relaxed">{offer.description || 'No campaign parameters provided.'}</p>
                          </div>
                          <div className="bg-[#FFCD40] text-slate-900 px-3.5 py-2.5 rounded-xl text-center shrink-0 border border-amber-300 font-black shadow-xs">
                            <span className="block text-xl leading-none font-black">{offer.offerValue}</span>
                            <span className="text-[9px] font-black uppercase tracking-wider opacity-80 block mt-1">
                              {offer.offerType === 'Percentage' ? '% OFF' : 'AED OFF'}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 border border-dashed border-mediumgray rounded-xl text-xs font-semibold text-gray-400 italic bg-lightgray/30">
                      No active offers scheduled on this date. Click "Create Offer Entry" above to add one.
                    </div>
                  )}
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {/* =========================================================
          VIEW 3: MODAL WINDOW COMPONENT (MOBILE RESPONSIVE)
          ========================================================= */}
      {showCreateModal && selectedService && (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl md:rounded-3xl w-full max-w-xl shadow-xl overflow-hidden border border-gray-100">
            
            <div className="p-4 md:p-5 border-b border-mediumgray flex items-center justify-between bg-lightgray">
              <div className="space-y-0.5">
                <h3 className="text-lg md:text-xl font-black text-logoHeading tracking-tight">Schedule Offer Campaign</h3>
                <p className="text-[10px] md:text-[11px] font-bold uppercase tracking-wider text-headerButtonColor">{selectedService.name}</p>
              </div>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 rounded-lg border border-mediumgray bg-white text-gray-400 hover:text-gray-700 transition cursor-pointer">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleCreateOfferSubmit} className="p-4 md:p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              
              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Offer Title Header</label>
                <input 
                  type="text" required disabled={isCreating}
                  placeholder="e.g., Flash Discount, Summer Deal"
                  value={formData.offerTitle}
                  onChange={(e) => setFormData({ ...formData, offerTitle: e.target.value })}
                  className="w-full border border-mediumgray bg-lightgray rounded-xl px-3 py-2.5 text-xs md:text-sm font-semibold focus:border-mainBrandColor outline-none focus:bg-white"
                />
              </div>

              <div className="space-y-1">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Campaign Specifications Summary</label>
                <textarea 
                  required rows={2} disabled={isCreating}
                  placeholder="Summarize target criteria details..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-mediumgray bg-lightgray rounded-xl px-3 py-2.5 text-xs md:text-sm font-semibold focus:border-mainBrandColor outline-none focus:bg-white resize-none"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 bg-lightgray border border-mediumgray rounded-xl p-3">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Deduction Type</span>
                  <div className="flex gap-2 mt-0.5">
                    <button
                      type="button" disabled={isCreating}
                      onClick={() => setFormData({ ...formData, offerType: OfferType.Percentage })}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-extrabold uppercase transition cursor-pointer flex items-center justify-center gap-1 ${
                        formData.offerType === OfferType.Percentage ? 'bg-mainBrandColor text-white shadow-xs' : 'bg-white border border-mediumgray text-gray-400'
                      }`}
                    >
                      <Percent className="w-3.5 h-3.5" /> Percent
                    </button>
                    <button
                      type="button" disabled={isCreating}
                      onClick={() => setFormData({ ...formData, offerType: OfferType.Flat })}
                      className={`flex-1 py-1.5 rounded-lg text-xs font-extrabold uppercase transition cursor-pointer flex items-center justify-center gap-1 ${
                        formData.offerType === OfferType.Flat ? 'bg-mainBrandColor text-white shadow-xs' : 'bg-white border border-mediumgray text-gray-400'
                      }`}
                    >
                      <CircleDollarSign className="w-3.5 h-3.5" /> AED Flat
                    </button>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                    {formData.offerType === OfferType.Percentage ? 'Discount Value (%)' : 'Deduction Value (AED)'}
                  </span>
                  <input 
                    type="number" required min={1} max={formData.offerType === OfferType.Percentage ? 100 : undefined} disabled={isCreating}
                    placeholder={formData.offerType === OfferType.Percentage ? 'e.g., 15' : 'e.g., 50'}
                    value={formData.offerValue || ''}
                    onChange={(e) => setFormData({ ...formData, offerValue: Number(e.target.value) || 0 })}
                    className="w-full border border-mediumgray bg-white rounded-xl px-3 py-1.5 text-xs font-bold outline-none focus:border-mainBrandColor mt-0.5"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">Start Date</label>
                  <input 
                    type="date" required disabled={isCreating}
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:border-mainBrandColor focus:bg-white"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-400">End Date</label>
                  <input 
                    type="date" required min={formData.startDate} disabled={isCreating}
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border border-mediumgray bg-lightgray rounded-xl px-3 py-2 text-xs font-bold text-gray-700 outline-none focus:border-mainBrandColor focus:bg-white"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-mediumgray mt-4">
                <button
                  type="button" onClick={() => setShowCreateModal(false)} disabled={isCreating}
                  className="border border-mediumgray bg-white px-4 py-2 rounded-xl text-xs uppercase tracking-wider font-bold text-gray-500 hover:bg-lightgray transition cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit" disabled={isCreating}
                  className="bg-logoHeading hover:opacity-90 text-white px-4 py-2 rounded-xl text-xs uppercase tracking-wider font-bold shadow-xs transition inline-flex items-center justify-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isCreating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                  Publish Offer
                </button>
              </div>

            </form>
          </div>
        </div>
      )}

    </div>
  );
}